// src/api-response.ts
function unwrapList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object" && Array.isArray(payload.data)) {
    return payload.data;
  }
  return [];
}
function toNumber(v) {
  if (v == null) return 0;
  return typeof v === "string" ? Number(v) : v;
}

// src/formatters/currency.ts
var NPR_SYMBOL = "\u0930\u0941.";
var NPR_SHORT = "Rs.";
function formatNPR(amount) {
  if (!Number.isFinite(amount)) return `${NPR_SYMBOL} 0`;
  const rounded = Math.round(amount);
  return `${NPR_SYMBOL} ${rounded.toLocaleString("en-IN")}`;
}
function formatNPRShort(amount) {
  if (!Number.isFinite(amount)) return `${NPR_SHORT} 0`;
  const rounded = Math.round(amount);
  return `${NPR_SHORT} ${rounded.toLocaleString("en-IN")}`;
}
function formatNPRCompact(amount) {
  if (!Number.isFinite(amount)) return `${NPR_SYMBOL} 0`;
  if (amount >= 1e7) {
    return `${NPR_SYMBOL} ${(amount / 1e7).toFixed(1).replace(/\.0$/, "")}Cr`;
  }
  if (amount >= 1e5) {
    return `${NPR_SYMBOL} ${(amount / 1e5).toFixed(1).replace(/\.0$/, "")}L`;
  }
  if (amount >= 1e3) {
    return `${NPR_SYMBOL} ${(amount / 1e3).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return formatNPR(amount);
}
function parseNPR(str) {
  if (!str) return 0;
  const cleaned = str.replace(/रु\.?|Rs\.?|NPR/gi, "").replace(/,/g, "").replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
  if (!cleaned) return 0;
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
}
function calcDiscountPercent(original, selling) {
  if (original <= 0 || selling >= original) return 0;
  return Math.round((original - selling) / original * 100);
}
function withVat(amount, vatRate = 0.13) {
  return Math.round(amount * (1 + vatRate));
}
function extractVat(vatInclusiveAmount, vatRate = 0.13) {
  const baseAmount = Math.round(vatInclusiveAmount / (1 + vatRate));
  return {
    baseAmount,
    vatAmount: vatInclusiveAmount - baseAmount
  };
}

// src/formatters/date.ts
function formatDate(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "\u2014";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}
function formatDateTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "\u2014";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
}
function formatDateShort(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "\u2014";
  return d.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit"
  });
}
function fromNow(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "\u2014";
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffSec = Math.floor(diffMs / 1e3);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);
  if (diffSec < 30) return "just now";
  if (diffSec < 60) return `${diffSec} seconds ago`;
  if (diffMin < 60) return diffMin === 1 ? "1 minute ago" : `${diffMin} minutes ago`;
  if (diffHour < 24) return diffHour === 1 ? "1 hour ago" : `${diffHour} hours ago`;
  if (diffDay < 7) return diffDay === 1 ? "yesterday" : `${diffDay} days ago`;
  if (diffWeek < 4) return diffWeek === 1 ? "1 week ago" : `${diffWeek} weeks ago`;
  if (diffMonth < 12) return diffMonth === 1 ? "1 month ago" : `${diffMonth} months ago`;
  return diffYear === 1 ? "1 year ago" : `${diffYear} years ago`;
}
function toBikramSambat(gregorianDate) {
  const d = typeof gregorianDate === "string" ? new Date(gregorianDate) : gregorianDate;
  if (isNaN(d.getTime())) return "\u2014";
  const BS_MONTHS = [
    "Baishakh",
    "Jestha",
    "Ashadh",
    "Shrawan",
    "Bhadra",
    "Ashwin",
    "Kartik",
    "Mangsir",
    "Poush",
    "Magh",
    "Falgun",
    "Chaitra"
  ];
  const gYear = d.getFullYear();
  const gMonth = d.getMonth();
  const gDay = d.getDate();
  let bsYear;
  const isAfterBaisakh = gMonth > 3 || gMonth === 3 && gDay >= 14;
  bsYear = isAfterBaisakh ? gYear + 57 : gYear + 56;
  const APRIL_14_DAY_OF_YEAR = 104;
  const dayOfYear = getDayOfYear(d);
  let bsDayOfYear;
  if (dayOfYear >= APRIL_14_DAY_OF_YEAR) {
    bsDayOfYear = dayOfYear - APRIL_14_DAY_OF_YEAR;
  } else {
    const prevYearDays = isLeapYear(gYear - 1) ? 366 : 365;
    bsDayOfYear = prevYearDays - APRIL_14_DAY_OF_YEAR + dayOfYear;
  }
  const bsMonthIndex = Math.min(Math.floor(bsDayOfYear / 30.5), 11);
  const bsDay = bsDayOfYear % 30 + 1;
  return `${bsYear} ${BS_MONTHS[bsMonthIndex]} ${String(bsDay).padStart(2, "0")}`;
}
function getBsYear(gregorianDate) {
  const gYear = gregorianDate.getFullYear();
  const gMonth = gregorianDate.getMonth();
  const gDay = gregorianDate.getDate();
  const isAfterBaisakh = gMonth > 3 || gMonth === 3 && gDay >= 14;
  return isAfterBaisakh ? gYear + 57 : gYear + 56;
}
function getDayOfYear(d) {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d.getTime() - start.getTime();
  const oneDay = 1e3 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
}
function isLeapYear(year) {
  return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
}
function formatOrderDate(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "\u2014";
  const now = /* @__PURE__ */ new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 864e5);
  const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  if (dateOnly.getTime() === today.getTime()) return `Today, ${time}`;
  if (dateOnly.getTime() === yesterday.getTime()) return `Yesterday, ${time}`;
  return `${formatDateShort(d)}, ${time}`;
}

// src/formatters/phone.ts
var NEPAL_COUNTRY_CODE = "+977";
function normalizePhone(phone) {
  return phone.replace(/\D/g, "");
}
function formatNepalPhone(phone) {
  if (!phone) return "";
  let digits = normalizePhone(phone);
  if (digits.startsWith("977")) {
    digits = digits.slice(3);
  }
  if (digits.length === 10) {
    const part1 = digits.slice(0, 2);
    const part2 = digits.slice(2, 6);
    const part3 = digits.slice(6, 10);
    return `${NEPAL_COUNTRY_CODE} ${part1}-${part2}-${part3}`;
  }
  if (digits.length === 7) {
    return `${NEPAL_COUNTRY_CODE} ${digits}`;
  }
  return `${NEPAL_COUNTRY_CODE} ${digits}`;
}
function maskPhone(phone) {
  if (!phone) return "";
  let digits = normalizePhone(phone);
  if (digits.startsWith("977")) {
    digits = digits.slice(3);
  }
  if (digits.length >= 7) {
    const visible_start = digits.slice(0, 3);
    const visible_end = digits.slice(-4);
    const maskLength = digits.length - 7;
    const mask = "*".repeat(Math.max(3, maskLength));
    return `${NEPAL_COUNTRY_CODE} ${visible_start}${mask}${visible_end}`;
  }
  return `${NEPAL_COUNTRY_CODE} ${digits}`;
}
function isValidNepalPhone(phone) {
  const digits = normalizePhone(phone).replace(/^977/, "");
  return /^(97|98)\d{8}$/.test(digits);
}
function extractLocalNumber(phone) {
  let digits = normalizePhone(phone);
  if (digits.startsWith("977")) {
    digits = digits.slice(3);
  }
  return digits;
}
function formatPhoneCompact(phone) {
  const digits = extractLocalNumber(phone);
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  return digits;
}

// src/constants/nepal-provinces.ts
var NEPAL_PROVINCES = [
  {
    id: 1,
    name: "Province No. 1",
    nameNp: "\u092A\u094D\u0930\u0926\u0947\u0936 \u0928\u0902. \u0967",
    headquarters: "Biratnagar",
    districts: 14,
    area_km2: 25905
  },
  {
    id: 2,
    name: "Madhesh Province",
    nameNp: "\u092E\u0927\u0947\u0936 \u092A\u094D\u0930\u0926\u0947\u0936",
    headquarters: "Janakpur",
    districts: 8,
    area_km2: 9661
  },
  {
    id: 3,
    name: "Bagmati Province",
    nameNp: "\u092C\u093E\u0917\u092E\u0924\u0940 \u092A\u094D\u0930\u0926\u0947\u0936",
    headquarters: "Hetauda",
    districts: 13,
    area_km2: 20300
  },
  {
    id: 4,
    name: "Gandaki Province",
    nameNp: "\u0917\u0923\u094D\u0921\u0915\u0940 \u092A\u094D\u0930\u0926\u0947\u0936",
    headquarters: "Pokhara",
    districts: 11,
    area_km2: 21504
  },
  {
    id: 5,
    name: "Lumbini Province",
    nameNp: "\u0932\u0941\u092E\u094D\u092C\u093F\u0928\u0940 \u092A\u094D\u0930\u0926\u0947\u0936",
    headquarters: "Butwal",
    districts: 12,
    area_km2: 22288
  },
  {
    id: 6,
    name: "Karnali Province",
    nameNp: "\u0915\u0930\u094D\u0923\u093E\u0932\u0940 \u092A\u094D\u0930\u0926\u0947\u0936",
    headquarters: "Birendranagar",
    districts: 10,
    area_km2: 27984
  },
  {
    id: 7,
    name: "Sudurpashchim Province",
    nameNp: "\u0938\u0941\u0926\u0942\u0930\u092A\u0936\u094D\u091A\u093F\u092E \u092A\u094D\u0930\u0926\u0947\u0936",
    headquarters: "Dhangadhi",
    districts: 9,
    area_km2: 19539
  }
];
function getProvinceById(id) {
  return NEPAL_PROVINCES.find((p) => p.id === id);
}
function getProvinceByName(name) {
  const lower = name.toLowerCase();
  return NEPAL_PROVINCES.find(
    (p) => p.name.toLowerCase() === lower || p.nameLocal?.toLowerCase() === lower
  );
}
var COD_AVAILABLE_PROVINCE_IDS = [1, 2, 3, 4, 5, 6, 7];
var PROVINCE_DELIVERY_HUBS = {
  1: ["Biratnagar", "Dharan", "Itahari", "Birtamod"],
  2: ["Janakpur", "Birgunj", "Rajbiraj", "Lahan"],
  3: ["Kathmandu", "Lalitpur", "Bhaktapur", "Hetauda", "Chitwan"],
  4: ["Pokhara", "Baglung", "Gorkha", "Damauli"],
  5: ["Butwal", "Bharatpur", "Nepalgunj", "Tulsipur"],
  6: ["Birendranagar", "Jumla", "Dailekh"],
  7: ["Dhangadhi", "Mahendranagar", "Dadeldhura"]
};

// src/constants/app-config.ts
var VAT_RATE = 0.13;
var TDS_RATE = 0.015;
var COMMISSION_RATES = {
  basic: 5,
  // 5% of order value
  professional: 3.5,
  // 3.5% of order value
  enterprise: 0
  // Custom rate — see vendor agreement
};
var MIN_COMMISSION_AMOUNT_NPR = 10;
var MAX_PRODUCT_IMAGES = 10;
var MAX_PRODUCT_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
var ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
var MAX_PRODUCT_VARIANTS = 50;
var MAX_PRODUCT_NAME_LENGTH = 200;
var MAX_PRODUCT_DESCRIPTION_LENGTH = 1e4;
var ORDER_CANCELLATION_WINDOW_HOURS = 2;
var MAX_ORDER_ITEMS = 50;
var MIN_ORDER_VALUE_NPR = 50;
var COD_AVAILABLE_PROVINCES = [1, 2, 3, 4, 5, 6, 7];
var COD_FEE_BY_PROVINCE = {
  1: 40,
  // Province 1 — Terai: 40, Hills: 60
  2: 30,
  // Madhesh — mostly Terai, accessible
  3: 30,
  // Bagmati — Kathmandu Valley: 30, Remote: 60
  4: 50,
  // Gandaki — Hilly
  5: 40,
  // Lumbini
  6: 80,
  // Karnali — remote, high altitude
  7: 60
  // Sudurpashchim
};
var FREE_COD_THRESHOLD_NPR = 3e3;
var MIN_PAYOUT_AMOUNT = 500;
var MAX_PAYOUT_AMOUNT_BASIC = 1e5;
var MAX_PAYOUT_AMOUNT_PROFESSIONAL = 5e5;
var PAYOUT_PROCESSING_DAYS = 3;
var PAYOUT_HOLD_DAYS = 7;
var REVIEW_WINDOW_DAYS = 30;
var MIN_REVIEW_LENGTH = 10;
var MAX_REVIEW_LENGTH = 1e3;
var ACCESS_TOKEN_EXPIRY_SECONDS = 15 * 60;
var REFRESH_TOKEN_EXPIRY_SECONDS = 30 * 24 * 60 * 60;
var OTP_EXPIRY_SECONDS = 5 * 60;
var OTP_RESEND_COOLDOWN_SECONDS = 60;
var DEFAULT_PAGE_SIZE = 20;
var MAX_PAGE_SIZE = 100;
var MIN_SEARCH_QUERY_LENGTH = 2;
var MAX_SEARCH_RESULTS = 50;
var MAX_KYC_FILE_SIZE_BYTES = 10 * 1024 * 1024;
var ALLOWED_KYC_TYPES = ["image/jpeg", "image/png", "application/pdf"];
var DELIVERY_WINDOW_DAYS = {
  kathmandu_valley: { min: 1, max: 3 },
  terai: { min: 2, max: 5 },
  hills: { min: 3, max: 7 },
  remote: { min: 5, max: 14 }
};
export {
  ACCESS_TOKEN_EXPIRY_SECONDS,
  ALLOWED_IMAGE_TYPES,
  ALLOWED_KYC_TYPES,
  COD_AVAILABLE_PROVINCES,
  COD_AVAILABLE_PROVINCE_IDS,
  COD_FEE_BY_PROVINCE,
  COMMISSION_RATES,
  DEFAULT_PAGE_SIZE,
  DELIVERY_WINDOW_DAYS,
  FREE_COD_THRESHOLD_NPR,
  MAX_KYC_FILE_SIZE_BYTES,
  MAX_ORDER_ITEMS,
  MAX_PAGE_SIZE,
  MAX_PAYOUT_AMOUNT_BASIC,
  MAX_PAYOUT_AMOUNT_PROFESSIONAL,
  MAX_PRODUCT_DESCRIPTION_LENGTH,
  MAX_PRODUCT_IMAGES,
  MAX_PRODUCT_IMAGE_SIZE_BYTES,
  MAX_PRODUCT_NAME_LENGTH,
  MAX_PRODUCT_VARIANTS,
  MAX_REVIEW_LENGTH,
  MAX_SEARCH_RESULTS,
  MIN_COMMISSION_AMOUNT_NPR,
  MIN_ORDER_VALUE_NPR,
  MIN_PAYOUT_AMOUNT,
  MIN_REVIEW_LENGTH,
  MIN_SEARCH_QUERY_LENGTH,
  NEPAL_PROVINCES,
  ORDER_CANCELLATION_WINDOW_HOURS,
  OTP_EXPIRY_SECONDS,
  OTP_RESEND_COOLDOWN_SECONDS,
  PAYOUT_HOLD_DAYS,
  PAYOUT_PROCESSING_DAYS,
  PROVINCE_DELIVERY_HUBS,
  REFRESH_TOKEN_EXPIRY_SECONDS,
  REVIEW_WINDOW_DAYS,
  TDS_RATE,
  VAT_RATE,
  calcDiscountPercent,
  extractLocalNumber,
  extractVat,
  formatDate,
  formatDateShort,
  formatDateTime,
  formatNPR,
  formatNPRCompact,
  formatNPRShort,
  formatNepalPhone,
  formatOrderDate,
  formatPhoneCompact,
  fromNow,
  getBsYear,
  getProvinceById,
  getProvinceByName,
  isValidNepalPhone,
  maskPhone,
  parseNPR,
  toBikramSambat,
  toNumber,
  unwrapList,
  withVat
};
